/**
 * Client-SDK specialization of the section-switch reason document schema
 * + typed refs via `createConverter`.
 */
import {
  collection,
  doc,
  getFirestore,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {
  SectionSwitchReasonDocSchema as SectionSwitchReasonDocBaseSchema,
  getSectionSwitchReasonDocPath,
  getSectionSwitchReasonsCollectionPath,
} from '../documents/sectionSwitchReason';
import {ClientTimestampSchema, createConverter} from './sdk';

/**
 * Stored reason with client-SDK `Timestamp`. `createdAt` is nullable
 * because Firestore emits local-write-pending docs with unresolved
 * `serverTimestamp()` sentinels as null — rejecting those during read
 * would break optimistic local snapshots. The persisted server shape
 * is always populated.
 */
export const SectionSwitchReasonDocSchema =
  SectionSwitchReasonDocBaseSchema.extend({
    createdAt: ClientTimestampSchema.nullable(),
  }).loose();

/** Inferred type (client-SDK timestamps). */
export type SectionSwitchReasonDoc = z.infer<
  typeof SectionSwitchReasonDocSchema
>;

const sectionSwitchReasonConverter = createConverter({
  schema: SectionSwitchReasonDocSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/**
 * Typed reference for the reasons collection of a course.
 *
 * @param args.courseId - The course id.
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 * @returns A `CollectionReference<SectionSwitchReasonDoc>`.
 */
export function getSectionSwitchReasonsCollectionRef({
  courseId,
  firestore = getFirestore(),
}: {
  courseId: string;
  firestore?: Firestore;
}): CollectionReference<SectionSwitchReasonDoc> {
  return collection(
    firestore,
    getSectionSwitchReasonsCollectionPath({courseId}),
  ).withConverter(sectionSwitchReasonConverter);
}

/**
 * Typed reference for a single reason doc.
 *
 * @param args.courseId - The course id.
 * @param args.reasonId - The reason doc id.
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 * @returns A `DocumentReference<SectionSwitchReasonDoc>`.
 */
export function getSectionSwitchReasonDocRef({
  courseId,
  reasonId,
  firestore = getFirestore(),
}: {
  courseId: string;
  reasonId: string;
  firestore?: Firestore;
}): DocumentReference<SectionSwitchReasonDoc> {
  return doc(
    firestore,
    getSectionSwitchReasonDocPath({courseId, reasonId}),
  ).withConverter(sectionSwitchReasonConverter);
}
